
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const EmailPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    analysis_emails: true,
    promotional_emails: true,
    unsubscribed: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      fetchPreferences();
    }
  }, [user]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_email_preferences')
        .select('*')
        .eq('user_id', user?.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "row not found"
        throw error;
      }

      if (data) {
        setPreferences({
          analysis_emails: data.analysis_emails,
          promotional_emails: data.promotional_emails,
          unsubscribed: data.unsubscribed
        });
      }
    } catch (error) {
      console.error('Error fetching email preferences:', error);
      toast.error("Failed to load email preferences.");
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (field: string, value: boolean) => {
    if (!user) return;
    
    // Update local state immediately for responsive UI
    setPreferences(prev => ({
      ...prev,
      [field]: value,
      // If unsubscribe is turned on, turn off other preferences
      ...(field === 'unsubscribed' && value ? { analysis_emails: false, promotional_emails: false } : {}),
      // If any preference is turned on, turn off unsubscribed
      ...(field !== 'unsubscribed' && value ? { unsubscribed: false } : {})
    }));

    try {
      setSaving(true);
      
      const updatedPreferences = {
        ...preferences,
        [field]: value,
        // Handle the logical relationships between preferences
        ...(field === 'unsubscribed' && value ? { analysis_emails: false, promotional_emails: false } : {}),
        ...(field !== 'unsubscribed' && value ? { unsubscribed: false } : {})
      };
      
      const { error } = await supabase
        .from('user_email_preferences')
        .upsert({
          user_id: user.id,
          ...updatedPreferences,
          updated_at: new Date().toISOString()
        });

      if (error) {
        throw error;
      }

      toast.success("Email preferences updated");
    } catch (error) {
      console.error('Error updating email preferences:', error);
      toast.error("Failed to update email preferences");
      
      // Revert local state on error
      fetchPreferences();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Email Preferences</CardTitle>
          <CardDescription>Loading email preferences...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Preferences</CardTitle>
        <CardDescription>Manage what emails you receive</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="analysis-emails">Learning Analysis Emails</Label>
            <p className="text-sm text-muted-foreground">
              Receive reports about your learning progress
            </p>
          </div>
          <Switch
            id="analysis-emails"
            checked={preferences.analysis_emails}
            onCheckedChange={(checked) => updatePreference('analysis_emails', checked)}
            disabled={saving || preferences.unsubscribed}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="promotional-emails">Feature Updates</Label>
            <p className="text-sm text-muted-foreground">
              Learn about new features and improvements
            </p>
          </div>
          <Switch
            id="promotional-emails"
            checked={preferences.promotional_emails}
            onCheckedChange={(checked) => updatePreference('promotional_emails', checked)}
            disabled={saving || preferences.unsubscribed}
          />
        </div>

        <div className="flex items-center justify-between pt-6 border-t">
          <div className="space-y-0.5">
            <Label htmlFor="unsubscribe" className="text-destructive">Unsubscribe from all emails</Label>
            <p className="text-sm text-muted-foreground">
              You won't receive any emails from us
            </p>
          </div>
          <Switch
            id="unsubscribe"
            checked={preferences.unsubscribed}
            onCheckedChange={(checked) => updatePreference('unsubscribed', checked)}
            disabled={saving}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailPreferences;
